import { createMap, forMember, mapFrom, mapWith, extend, constructUsing } from "@automapper/core";
import { TransactionHistory, Transaction, TransactionType } from "../../contract/pam.js";
import { userLabelViewModelTypeName, userLabelTypeName } from "../label/constants.js";
import { formatTimestamp } from "../../utils/date.js";
import "react-intl";
import { TransactionViewModelKeysTypeName, TransactionQueryFieldsTypeName, TransactionColumnSettingsKeysTypeName } from "./constants.js";
import { StatusLog, TransactionDuration, TransactionViewModel, transactionProductTypeMapper, TransactionDownloadModel } from "./types.js";
import { getTransactionDuration } from "./utils.js";
const transactionMappingKeys = {
  viewModelKeys: "Transaction.ViewModelKeys",
  downloadFields: "Transaction.DownloadFields"
};
class TransactionMapping {
  createMapping(mapper) {
    createMap(
      mapper,
      TransactionHistory,
      StatusLog,
      forMember(
        (vm) => vm.status,
        mapFrom((m) => m.status)
      ),
      forMember(
        (vm) => vm.logged_at,
        mapFrom((m) => m.logged_at)
      )
    );
    createMap(
      mapper,
      Transaction,
      TransactionDuration,
      forMember(
        (vm) => vm.history,
        mapWith(StatusLog, TransactionHistory, (m) => m.status_log)
      ),
      forMember(
        (vm) => vm.started_at,
        mapFrom((m) => m.transaction_started_ts)
      )
    );
    createMap(
      mapper,
      Transaction,
      TransactionViewModel,
      forMember(
        (vm) => vm.amount,
        mapFrom((m) => m.amount)
      ),
      forMember(
        (vm) => vm.duration,
        mapWith(TransactionDuration, Transaction, (m) => m)
      ),
      forMember(
        (vm) => vm.amount_without_currency,
        mapFrom((m) => m.amount)
      ),
      forMember(
        (vm) => vm.counterpart_player_id,
        mapFrom((m) => {
          const paymentDescription = m.payment_method_description;
          const parts = paymentDescription?.split("_");
          return parts?.length > 0 ? parts[0] : null;
        })
      ),
      forMember(
        (vm) => vm.created_by_uid,
        mapFrom((m) => m.created_by_uid)
      ),
      forMember(
        (vm) => vm.currency,
        mapFrom((m) => m.currency)
      ),
      forMember(
        (vm) => vm.current_balance,
        mapFrom((m) => m.current_balance)
      ),
      forMember(
        (vm) => vm.email,
        mapFrom((m) => m.contact?.email)
      ),
      forMember(
        (vm) => vm.id,
        mapFrom((m) => m.transaction_id)
      ),
      forMember(
        (vm) => vm.payment_method_name,
        mapFrom((m) => m.payment_method_name)
      ),
      forMember(
        (vm) => vm.payment_method_description,
        mapFrom((m) => m.payment_method_description)
      ),
      forMember(
        (vm) => vm.player_country,
        mapFrom((m) => m.iso_alpha2_country_code)
      ),
      forMember(
        (vm) => vm.status_log,
        mapWith(StatusLog, TransactionHistory, (m) => m.status_log)
      ),
      forMember(
        (vm) => vm.transaction_id,
        mapFrom((m) => m.transaction_id)
      ),
      forMember(
        (vm) => vm.transaction_type,
        mapFrom((m) => m.type)
      ),
      forMember(
        (vm) => vm.transaction_started_ts,
        mapFrom((m) => m.transaction_started_ts)
      ),
      forMember(
        (vm) => vm.transaction_updated_ts,
        mapFrom((m) => m.transaction_updated_ts)
      ),
      forMember(
        (vm) => vm.transaction_status,
        mapFrom((m) => m.transaction_status)
      ),
      forMember(
        (vm) => vm.uid,
        mapFrom((m) => m.uid)
      ),
      forMember(
        (vm) => vm.username,
        mapFrom((m) => m.username)
      ),
      forMember(
        (vm) => vm.phone,
        mapFrom((m) => m?.contact?.mobile?.full_number)
      ),
      forMember(
        (vm) => vm.referrer_player_id,
        mapFrom((m) => m?.referrer_player_id)
      ),
      forMember(
        (vm) => vm.withdrawal_id,
        mapFrom((m) => m.transaction_id)
      ),
      forMember(
        (vm) => vm.register_marketing_code,
        mapFrom((m) => m.register_marketing_code)
      ),
      forMember(
        (vm) => vm.user_labels,
        mapWith(userLabelViewModelTypeName, userLabelTypeName, (m) => m.user_labels)
      ),
      forMember(
        (vm) => vm.current_casino_coin_balance,
        mapFrom((m) => m.current_casino_coin_balance)
      ),
      forMember(
        (vm) => vm.last_reason,
        mapFrom((m) => m.last_reason)
      ),
      forMember(
        (vm) => vm.product_type,
        mapFrom((m) => transactionProductTypeMapper?.[m?.type])
      ),
      forMember(
        (vm) => vm.assignee.id,
        mapFrom((m) => m.assigned_user_id)
      ),
      forMember(
        (vm) => vm.retries,
        mapFrom((m) => m.retries)
      ),
      forMember(
        (vm) => vm.error_code,
        mapFrom((m) => m.error_code)
      ),
      forMember(
        (vm) => vm.error_json,
        mapFrom((m) => m.error_json)
      ),
      forMember(
        (vm) => vm.retry_handled_at,
        mapFrom((m) => m.retry_handled_at)
      ),
      forMember(
        (vm) => vm.turnover_rate,
        mapFrom((m) => m.type === TransactionType.Credit ? m.turnover_rate : null)
      ),
      forMember(
        (vm) => vm.withdrawable_balance,
        mapFrom((m) => m.withdrawable_balance)
      ),
      forMember(
        (vm) => vm.valid_bet,
        mapFrom((m) => m.valid_bet)
      ),
      forMember(
        (vm) => vm.ptp_counterparty_uid,
        mapFrom((m) => m.ptp_counterparty_uid)
      )
    );
    createMap(
      mapper,
      Transaction,
      TransactionDownloadModel,
      extend(Transaction, TransactionViewModel),
      forMember(
        (vm) => vm.transaction_started_ts,
        mapFrom((m) => formatTimestamp(m.transaction_started_ts, "date-time"))
      ),
      forMember(
        (vm) => vm.transaction_updated_ts,
        mapFrom((m) => formatTimestamp(m.transaction_updated_ts, "date-time"))
      ),
      forMember(
        (vm) => vm.user_labels,
        mapFrom((m) => m.user_labels?.map((label) => label?.name)?.join())
      ),
      forMember(
        (vm) => vm.duration,
        mapFrom((m) => {
          const started_at = m.transaction_started_ts;
          const history = m.status_log;
          return getTransactionDuration({ history, started_at });
        })
      ),
      forMember(
        (vm) => vm.assignee,
        mapFrom((m) => m?.assigned_user_id)
      ),
      forMember(
        (vm) => vm.ptp_counterparty_uid,
        mapFrom((m) => m.ptp_counterparty_uid)
      )
    );
    createMap(
      mapper,
      TransactionViewModelKeysTypeName,
      TransactionQueryFieldsTypeName,
      constructUsing((m, _) => {
        const mapper2 = {
          id: ["transaction_id"],
          created_by_uid: ["created_by_uid"],
          status_log: ["status_log.logged_at.seconds", "status_log.status"],
          amount: ["amount", "currency"],
          amount_without_currency: ["amount"],
          counterpart_player_id: ["payment_method_description"],
          currency: ["currency"],
          current_balance: ["current_balance"],
          current_casino_coin_balance: ["current_casino_coin_balance"],
          duration: ["status_log.status", "status_log.logged_at.seconds", "transaction_started_ts"],
          email: ["contact.email", "email"],
          payment_method_description: ["payment_method_description"],
          payment_method_name: ["payment_method_name"],
          phone: ["contact.mobile"],
          player_country: ["iso_alpha2_country_code"],
          referrer_player_id: ["referrer_player_id"],
          transaction_id: ["transaction_id"],
          transaction_started_ts: ["transaction_started_ts"],
          transaction_status: ["transaction_status", "last_reason"],
          transaction_type: ["type"],
          type: ["type"],
          uid: ["uid"],
          username: ["username"],
          withdrawal_id: ["transaction_id"],
          transaction_updated_ts: ["transaction_updated_ts"],
          register_marketing_code: ["register_marketing_code"],
          user_labels: [
            "user_labels.id",
            "user_labels.name",
            "user_labels.group.id",
            "user_labels.group.name",
            "user_labels.group.color"
          ],
          p2p_direction: ["type"],
          assignee: ["assigned_user_id"],
          last_reason: ["last_reason"],
          retries: ["retries"],
          product_type: ["type"],
          error_code: ["error_code"],
          error_json: ["error_json"],
          retry_handled_at: ["retry_handled_at"],
          turnover_rate: ["turnover_rate"],
          withdrawable_balance: ["withdrawable_balance"],
          transfer_amount: ["amount"],
          valid_bet: ["valid_bet"],
          ptp_counterparty_uid: ["ptp_counterparty_uid"],
          license_type: ["license_type"],
          brand: ["brand"]
        };
        return [...new Set(m.flatMap((i) => mapper2[i]))];
      })
    );
    createMap(
      mapper,
      transactionMappingKeys.viewModelKeys,
      transactionMappingKeys.downloadFields,
      constructUsing((m, _) => {
        const mapper2 = {
          id: "transaction_id",
          created_by_uid: "created_by_uid",
          status_log: null,
          amount: "amount",
          amount_without_currency: "amount",
          counterpart_player_id: "payment_method_description",
          currency: "currency",
          current_balance: "current_balance",
          current_casino_coin_balance: "current_casino_coin_balance",
          duration: null,
          email: "email",
          payment_method_description: "payment_method_description",
          payment_method_name: "payment_method_name",
          phone: "contact.mobile.full_number",
          player_country: "iso_alpha2_country_code",
          referrer_player_id: "referrer_player_id",
          transaction_id: "transaction_id",
          transaction_started_ts: "transaction_started_ts.seconds",
          transaction_status: "transaction_status",
          transaction_type: "type",
          type: "type",
          uid: "uid",
          username: "username",
          withdrawal_id: "transaction_id",
          transaction_updated_ts: "transaction_updated_ts.seconds",
          register_marketing_code: "register_marketing_code",
          user_labels: "user_labels.name",
          p2p_direction: "type",
          assignee: "assigned_user_id",
          last_reason: "last_reason",
          retries: "retries",
          product_type: "type",
          error_code: "error_code",
          error_json: "error_json",
          retry_handled_at: "retry_handled_at.seconds",
          turnover_rate: "turnover_rate",
          withdrawable_balance: "withdrawable_balance",
          transfer_amount: "amount",
          valid_bet: "valid_bet",
          ptp_counterparty_uid: "ptp_counterparty_uid",
          license_type: "license_type",
          brand: "brand"
        };
        return mapper2[m];
      })
    );
    createMap(
      mapper,
      TransactionViewModelKeysTypeName,
      TransactionColumnSettingsKeysTypeName,
      constructUsing((m) => {
        return m;
      })
    );
    createMap(
      mapper,
      TransactionColumnSettingsKeysTypeName,
      TransactionViewModelKeysTypeName,
      constructUsing((m) => {
        return m === "__type" ? void 0 : m;
      })
    );
  }
}
export {
  TransactionMapping,
  transactionMappingKeys
};
//# sourceMappingURL=mapper.js.map
